import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import {
  areTypedValuesEquivalent,
  coerceToPrimitive,
  flattenValue,
  isTypedValueEmpty,
} from 'notebook/tables/typed-values/DataTypes';
import type {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {extractComparableMinMaxValue} from 'procedures/utils/procedureParameterMinMax';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

import {
  compareValueToRange,
  isComparableTypedValue,
  isOutOfRange,
  type MinMaxTypedValue,
  type RangeComparison,
} from './PMEIParameterRange';

/**
 * Whether a value is actually recorded — non-null and not the type's empty sentinel. The single
 * source of truth for "does this value count?"
 */
export function isPresentTypedValue(value: TypedValue | null | undefined): value is TypedValue {
  return value != null && !isTypedValueEmpty(value);
}

/**
 * Whether two data-parameter values count as the same, for both the color grade and the tooltip copy.
 *
 * For float and datetime parameters, "matches the setpoint" must be a numeric comparison, using
 * the same normalization (parseTextAsFloat / epoch millis via extractComparableMinMaxValue) that
 * the min/max range check uses — otherwise a value like `37.0` would be graded as differing from a
 * setpoint of `37`, contradicting the range check that treats them as equal. Text, selector, and
 * entity values keep areTypedValuesEquivalent, where representation/id equality is the correct
 * semantic and numeric coercion would be wrong.
 */
function valuesMatchForComparison(a: TypedValue, b: TypedValue): boolean {
  const numericA = numericValueForMatch(a);
  const numericB = numericValueForMatch(b);
  if (numericA != null && numericB != null) {
    return numericA === numericB;
  }
  return areTypedValuesEquivalent(a, b);
}

/** Comparable number for float/datetime values; null for other types or unparseable values. */
function numericValueForMatch(value: TypedValue): number | null {
  if (value.type === FieldType.FLOAT || value.type === FieldType.DATETIME) {
    return extractComparableMinMaxValue(value);
  }
  return null;
}

export enum CellColorGrade {
  NONE = 'none', // no background — empty cell, or >1 planned value so grading is skipped
  UNPLANNED = 'unplanned', // gray (#F0F1F2); value recorded with nothing planned
  MISMATCH = 'mismatch', // yellow (#FCF8D6); value differs from the single planned value
  OUT_OF_RANGE = 'outOfRange', // orange (#FFD8A8); data-parameter value outside min/max
  STRICT_MISMATCH = 'strictMismatch', // orange (#FFD8A8); exact deviation (equipment model/value)
  MATCH = 'match', // no background override; value satisfies the plan
}

/**
 * Compares one confirmed cell value against its single planned reference and returns the grade that
 * value earns: MATCH when it satisfies the plan, or a deviation grade (MISMATCH, OUT_OF_RANGE, or
 * STRICT_MISMATCH) describing how it falls short. Each dimension supplies the comparator that encodes
 * its own rules, so `gradeDimension` stays generic and every grade is chosen here in the module —
 * callers never pass colors.
 */
type GradeComparator = (cellValue: TypedValue, reference: TypedValue) => CellColorGrade;

/**
 * Severity ordering used to pick the worst grade — both across multiple readings within a dimension
 * and across dimensions (`combineGrades`). Orange deviations outrank yellow, which outranks gray,
 * which outranks a plain match; NONE (ungraded) is lowest. OUT_OF_RANGE and STRICT_MISMATCH share a
 * tier because they never arise in the same dimension.
 */
const GRADE_SEVERITY: Record<CellColorGrade, number> = {
  [CellColorGrade.NONE]: 0,
  [CellColorGrade.MATCH]: 1,
  [CellColorGrade.UNPLANNED]: 2,
  [CellColorGrade.MISMATCH]: 3,
  [CellColorGrade.OUT_OF_RANGE]: 4,
  [CellColorGrade.STRICT_MISMATCH]: 4,
};

function moreSevereGrade(a: CellColorGrade, b: CellColorGrade): CellColorGrade {
  return GRADE_SEVERITY[a] >= GRADE_SEVERITY[b] ? a : b;
}

/**
 * The shared grading ladder for a single dimension:
 * - no confirmed values, or more than one planned value → NONE (nothing meaningful to grade);
 * - no planned value → UNPLANNED (confirmed value present but nothing planned);
 * - otherwise grade every confirmed reading against the single reference with `compare` and keep the
 *   worst grade, so one deviating reading colors the whole cell.
 */
function gradeDimension(
  cellValues: Array<TypedValue | null>,
  referenceValues: Array<TypedValue | null>,
  compare: GradeComparator
): CellColorGrade {
  const nonEmptyCell = cellValues.filter((v) => isPresentTypedValue(v));
  if (nonEmptyCell.length === 0) {
    return CellColorGrade.NONE;
  }

  if (hasMultiplePlannedValues(referenceValues)) {
    // With more than one planned value there is no single target to compare against, so there is
    // nothing meaningful to grade. Kept in lockstep with the tooltip, which bails on the same check.
    return CellColorGrade.NONE;
  }

  const nonEmptyRef = referenceValues.filter((v) => isPresentTypedValue(v));

  if (nonEmptyRef.length === 0) {
    return CellColorGrade.UNPLANNED;
  }

  const reference = nonEmptyRef[0];
  return nonEmptyCell
    .map((v) => compare(v, reference))
    .reduce((worst, grade) => moreSevereGrade(worst, grade), CellColorGrade.MATCH);
}

/**
 * Worst-case aggregation of independently graded dimensions (material and amount), using the same
 * severity ordering `gradeDimension` applies across readings.
 */
function combineGrades(...grades: Array<CellColorGrade>): CellColorGrade {
  return grades.reduce((worst, grade) => moreSevereGrade(worst, grade), CellColorGrade.NONE);
}

/** Amount mismatches are ignored, but a confirmed amount with no planned amount still grades UNPLANNED. */
const gradedIgnoringAmountMismatch: GradeComparator = () => CellColorGrade.MATCH;

/** Turns a "do these satisfy the plan?" predicate into the MATCH/MISMATCH (yellow) comparator it implies. */
function gradedByEquivalence(
  equivalent: (cellValue: TypedValue, reference: TypedValue) => boolean
): GradeComparator {
  return (cellValue, reference) =>
    equivalent(cellValue, reference) ? CellColorGrade.MATCH : CellColorGrade.MISMATCH;
}

/**
 * How a single data-parameter value differs from plan, or null when it satisfies the plan.
 */
export type DataParameterDifference = {kind: 'outOfRange'; comparison: RangeComparison} | {kind: 'mismatch'};

/**
 * Classifies one data-parameter value against its single planned setpoint and optional bounds: a
 * value outside the numeric/datetime range is flagged first (it outranks a plain mismatch), then a
 * value that differs from the setpoint, otherwise null when it satisfies the plan.
 */
export function classifyDataParameterDifference(
  value: TypedValue,
  reference: TypedValue,
  minimum: MinMaxTypedValue,
  maximum: MinMaxTypedValue
): DataParameterDifference | null {
  if (isComparableTypedValue(value)) {
    const comparison = compareValueToRange(value, minimum, maximum);
    if (isOutOfRange(comparison)) {
      return {kind: 'outOfRange', comparison};
    }
  }
  if (!valuesMatchForComparison(value, reference)) {
    return {kind: 'mismatch'};
  }
  return null;
}

function gradedByRange(minimum: MinMaxTypedValue, maximum: MinMaxTypedValue): GradeComparator {
  return (cellValue, reference) => {
    const difference = classifyDataParameterDifference(cellValue, reference, minimum, maximum);
    if (difference == null) {
      return CellColorGrade.MATCH;
    }
    return difference.kind === 'outOfRange' ? CellColorGrade.OUT_OF_RANGE : CellColorGrade.MISMATCH;
  };
}

/**
 * Computes the color grade for a data-parameter cell (Confirmed, Measured) based on how its values
 * relate to the reference (setpoint) values and the optional numeric/datetime range.
 *
 * @param cellValues - The recorded values for the cell (may include empty/null entries).
 * @param plannedValues - The planned (setpoint) values for the parameter.
 * @param minimum - Optional lower bound; null/undefined means unbounded.
 * @param maximum - Optional upper bound; null/undefined means unbounded.
 */
export function computeDataParameterCellColorGrade(
  cellValues: Array<TypedValue | null>,
  plannedValues: Array<TypedValue | null>,
  minimum: MinMaxTypedValue = null,
  maximum: MinMaxTypedValue = null
): CellColorGrade {
  return gradeDimension(cellValues, plannedValues, gradedByRange(minimum, maximum));
}

/**
 * Entity-link equality by underlying ID rather than display name. Two links pointing at different
 * entities that happen to share a display name must grade as a mismatch.
 */
function areEntityEquivalentById(a: TypedValue, b: TypedValue): boolean {
  return isEqual(entityIdOf(a), entityIdOf(b));
}

/**
 * The underlying entity id of a link value (ids, not display names). The comparison basis for
 * `areEntityEquivalentById`.
 */
function entityIdOf(value: TypedValue): unknown {
  return coerceToPrimitive(flattenValue(value), {shouldUseIds: true});
}

/**
 * A material-input value has two dimensions — the material (an entity link or free text) and its
 * optional amount.
 */
export interface MaterialInputValue {
  material: TypedValue;
  amount: TypedValue | null;
}

/** Whether a material-input value recorded any content at all (a material, an amount, or both). */
export function isRecordedMaterialInputValue(value: MaterialInputValue): boolean {
  return isPresentTypedValue(value.material) || isPresentTypedValue(value.amount);
}

/**
 * Whether a parameter plans more than one value, which neither the color grade nor the tooltip can
 * support: there is no single planned value to compare a recorded cell against. Both
 * `PMEICellColorGrade` and `PMEICellTooltipContent` consult this (or its material-input counterpart)
 * first and bail, so such a parameter never picks up a cell color or an explanatory message.
 *
 * In practice this is how plate-map parameters present, but the rule is the value count, not the
 * parameter being a plate map.
 */
export function hasMultiplePlannedValues(plannedValues: Array<TypedValue | null>): boolean {
  return plannedValues.filter((v) => isPresentTypedValue(v)).length > 1;
}

/**
 * The material-input counterpart to `hasMultiplePlannedValues`, counting whole planned *values*
 * rather than either dimension: a plan of `[{glucose, 720 mL}, {sucrose, —}]` plans two values but
 * only one amount, so a per-dimension count would skip the material and still grade the amount
 * against `720 mL`. Counting whole values skips the cell entirely, so neither the material nor the
 * amount is graded or described.
 */
export function hasMultiplePlannedMaterialInputValues(plannedValues: Array<MaterialInputValue>): boolean {
  return plannedValues.filter((v) => isRecordedMaterialInputValue(v)).length > 1;
}

/**
 * Whether a confirmed material satisfies its planned material. Entity materials are compared by
 * underlying entity id (not display name, so two distinct entities sharing a name still deviate);
 * text materials are compared by representation. This is the single source of truth for "does the
 * material satisfy the plan?" that both the color grade and the differences tooltip consume.
 */
export function confirmedMaterialMatchesPlan(
  confirmed: TypedValue,
  planned: TypedValue,
  isEntity: boolean
): boolean {
  return isEntity
    ? areEntityEquivalentById(confirmed, planned)
    : areTypedValuesEquivalent(confirmed, planned);
}

/**
 * Color grade for a material-input Confirmed cell. The material dimension is fully graded; the amount
 * dimension only surfaces UNPLANNED (gray) when a confirmed amount was recorded with no planned
 * amount — amount mismatches are intentionally ignored. Entity materials are compared by entity id;
 * text materials are compared by representation.
 *
 * The cell is left ungraded (NONE/MATCH) when everything matches, the confirmed cell is empty, or
 * the parameter plans more than one value.
 */
export function computeMaterialInputCellColorGrade(
  confirmedValues: Array<MaterialInputValue>,
  plannedValues: Array<MaterialInputValue>,
  isEntity: boolean
): CellColorGrade {
  if (hasMultiplePlannedMaterialInputValues(plannedValues)) {
    return CellColorGrade.NONE;
  }

  const materialGrade = gradeDimension(
    confirmedValues.map((v) => v.material),
    plannedValues.map((v) => v.material),
    gradedByEquivalence((cell, ref) => confirmedMaterialMatchesPlan(cell, ref, isEntity))
  );
  const amountGrade = gradeDimension(
    confirmedValues.map((v) => v.amount),
    plannedValues.map((v) => v.amount),
    gradedIgnoringAmountMismatch
  );
  return combineGrades(materialGrade, amountGrade);
}

/**
 * A single equipment value: the equipment link/text itself plus, for entity equipment, the id of
 * the entity's linked model (its "Model" tag). `modelId` is null for text equipment or an entity
 * with no model.
 */
export interface EquipmentValue {
  equipment: TypedValue;
  modelId: string | null;
}

/**
 * More than one planned equipment is malformed data rather than the ungraded case it legitimately is for
 * data parameters and material inputs. Both the color grade and the tooltip assert it, so the two cannot
 * disagree.
 *
 * Counts only the values that recorded equipment, matching what `gradeDimension` compares against:
 * an empty entry is padding, not a second plan.
 */
export function assertAtMostOnePlannedEquipment(
  parameterName: string,
  plannedValues: Array<EquipmentValue>
): void {
  const plannedCount = plannedValues.filter((v) => isPresentTypedValue(v.equipment)).length;
  assert(
    plannedCount <= 1,
    `Expected at most one planned equipment for ${parameterName}, got ${plannedCount}`
  );
}

/** How a confirmed equipment value differs from the planned equipment; a model mismatch is reported alone. */
export type EquipmentDifference = {kind: 'modelMismatch'} | {kind: 'equipmentMismatch'};

/**
 * Classifies one confirmed equipment value against the single planned one, or null when it matches.
 * Entity equipment is checked model-first and then by entity id, so two distinct entities sharing a
 * display name still deviate; text equipment has no model, so only the value itself can differ.
 */
export function classifyEquipmentDifference(
  confirmed: EquipmentValue,
  planned: EquipmentValue,
  isEntity: boolean
): EquipmentDifference | null {
  if (!isEntity) {
    return areTypedValuesEquivalent(confirmed.equipment, planned.equipment)
      ? null
      : {kind: 'equipmentMismatch'};
  }
  if (isModelMismatch(confirmed.modelId, planned.modelId)) {
    return {kind: 'modelMismatch'};
  }
  return areEntityEquivalentById(confirmed.equipment, planned.equipment) ? null : {kind: 'equipmentMismatch'};
}

/**
 * Color grade for an equipment Confirmed cell (a single dimension — unlike material inputs there is
 * no amount). The rules differ by the equipment version's data type:
 *
 * Entity equipment (`isEntity` true) — orange STRICT_MISMATCH outranks yellow MISMATCH:
 *   - STRICT_MISMATCH: the confirmed equipment entity's model differs from the planned entity's model.
 *   - MISMATCH: the confirmed equipment entity differs from the planned entity, by id (not name).
 *
 * Text equipment (`isEntity` false) — there is no model, so any difference in the equipment value
 * itself is the strict deviation:
 *   - STRICT_MISMATCH: confirmed value differs from planned value.
 *
 * In both cases `gradeDimension` still yields UNPLANNED (confirmed present, nothing planned) and
 * leaves the cell ungraded (NONE/MATCH) when everything matches or the confirmed cell is empty.
 *
 * Throws on more than one planned equipment
 */
export function computeEquipmentCellColorGrade(
  parameterName: string,
  confirmedValues: Array<EquipmentValue>,
  plannedValues: Array<EquipmentValue>,
  isEntity: boolean
): CellColorGrade {
  assertAtMostOnePlannedEquipment(parameterName, plannedValues);
  return gradeDimension(
    confirmedValues.map((v) => v.equipment),
    plannedValues.map((v) => v.equipment),
    gradedByEquipmentDifference([...plannedValues, ...confirmedValues], isEntity)
  );
}

/**
 * Colors each `classifyEquipmentDifference` verdict: a differing model is always STRICT_MISMATCH; a
 * differing value is MISMATCH for entity equipment and STRICT_MISMATCH for text, which has no model.
 */
function gradedByEquipmentDifference(values: Array<EquipmentValue>, isEntity: boolean): GradeComparator {
  // Key by the TypedValue itself: the comparator receives the same object references that were put
  // into `values` (gradeDimension only filters them), so we can recover each value's modelId
  // directly rather than re-deriving an entity id.
  const valueByEquipment = new Map(values.map((v) => [v.equipment, v] as const));
  const gradingValueFor = (equipment: TypedValue): EquipmentValue =>
    valueByEquipment.get(equipment) ?? {equipment, modelId: null};
  return (cellValue, reference) => {
    const difference = classifyEquipmentDifference(
      gradingValueFor(cellValue),
      gradingValueFor(reference),
      isEntity
    );
    if (difference == null) {
      return CellColorGrade.MATCH;
    }
    if (difference.kind === 'modelMismatch') {
      return CellColorGrade.STRICT_MISMATCH;
    }
    return isEntity ? CellColorGrade.MISMATCH : CellColorGrade.STRICT_MISMATCH;
  };
}

/**
 * Whether a confirmed equipment entity's model differs from the planned equipment entity's model.
 * With no planned model there is nothing to deviate from, so it is never a mismatch (an absent
 * planned equipment already surfaces as UNPLANNED). When a planned model exists, any confirmed model
 * that differs from it — including a confirmed entity with no model at all — is a mismatch.
 */
function isModelMismatch(confirmedModelId: string | null, plannedModelId: string | null): boolean {
  return plannedModelId != null && confirmedModelId !== plannedModelId;
}
